import { useCallback } from "react";

/**
 * Sanitizes an ID string to be safe for IndexedDB keys
 * @param {string} id - The ID to sanitize
 * @returns {string} - Sanitized ID
 */
export const sanitizeId = (id) => {
  if (!id || typeof id !== "string") {
    return "currentCanvas";
  }
  // Remove any characters that could cause IndexedDB key issues
  const sanitized = id.replace(/[^a-zA-Z0-9_-]/g, "_");
  if (sanitized !== id) {
    // Optional: Log warning about ID sanitization
  }
  return sanitized;
};

/**
 * Detects the current browser zoom level
 * @param {Function} setBrowserZoomLevel - State setter for zoom level
 * @returns {Function} - Callback function to detect zoom
 */
export const useDetectBrowserZoom = (setBrowserZoomLevel) => {
  return useCallback(() => {
    // Method 1: Using visual viewport API (modern browsers)
    if (window.visualViewport) {
      const zoom = window.visualViewport.scale;
      setBrowserZoomLevel(zoom);
      return zoom;
    }

    // Method 2: Using devicePixelRatio and screen dimensions (fallback)
    const devicePixelRatio = window.devicePixelRatio || 1;
    const screenWidth = window.screen.width;
    const windowWidth = window.innerWidth;
    const calculatedZoom = (screenWidth / windowWidth) * devicePixelRatio;

    // Clamp to reasonable range
    const clampedZoom = Math.max(0.25, Math.min(calculatedZoom, 5));
    setBrowserZoomLevel(clampedZoom);
    return clampedZoom;
  }, [setBrowserZoomLevel]);
};

/**
 * Ensures smooth scrolling to the bottom of the canvas container
 * @returns {Function} - Callback function to scroll to bottom
 */
export const useEnsureScrollToBottom = () => {
  return useCallback(() => {
    const canvasContainer = document.querySelector(".canvas-container");
    if (canvasContainer) {
      // Calculate the maximum scroll position based on zoom level
      const maxScrollTop =
        canvasContainer.scrollHeight - canvasContainer.clientHeight;

      // Smooth scroll to bottom
      canvasContainer.scrollTo({
        top: maxScrollTop,
        behavior: "smooth",
      });
    }
  }, []);
};

/**
 * Get the root font size in pixels
 * @returns {number} Root font size in pixels (defaults to 16 or 40 on error)
 */
export const getRootFontSizePx = () => {
  try {
    const value = parseFloat(
      getComputedStyle(document.documentElement).fontSize
    );
    return isNaN(value) ? 16 : value;
  } catch (e) {
    return 40;
  }
};
